/**
 * The card as it goes to the printer: the two sides of the sheet at their
 * real size, pictures and notes only - no numbers or outlines, and the fold
 * guides if wanted.
 *
 * A person opens it from the editor and prints it from the browser. It is
 * drawn by the same code as the editor and the preview - and as the PDF, which
 * is made on the device from the same SheetFace (card-pdf.tsx) - so what prints
 * is what was on screen.
 *
 * Two-sided printing (2026-10-10): a web page cannot switch it on - the print
 * dialog offers "Two-sided" only when the printer tells the computer it can.
 * So besides printing both sides at once, each side can be printed on its own
 * (print side 1, put the sheet back, print side 2), and the page is exactly
 * two pages long (no trailing blank page to upset two-sided printing).
 *
 * 2026-10-17: the instructions are four small pictures across the page - paper, actual
 * size, both sides, fold - and "Next: how to fold" leads on to the folding guide; the
 * long text and the list of folds are gone ("no one will read it").
 */
import { useEffect, useMemo, useState } from 'react';
import { withShapes, type PrintGuides, type ProjectDocument, type TemplateDocument, type ZoneShapes, type ZoneText } from '@foldmycard/scene';
import { api, type ImageItem, type ProjectItem } from '../api.ts';
import { TipButton } from '../tip.tsx';
import { codeFor, guestsOn, personalise, sampleCode, useEvent } from '../invite.tsx';
import { SignOutButton } from '../idle.tsx';
import { SheetFace, shownImage, useLayouts, type ShownImage } from '../card-view.tsx';
import { symbolsOf } from '../symbol.tsx';
import { Link, useLocation } from '../router.tsx';
import { envelopeFits } from '../envelope/designs.ts';
import { t } from '../strings.ts';

/** The four steps as small pictures: the paper, actual size, both sides, the folding. */
function PrintPictogram({ k }: { k: 'paper' | 'scale' | 'duplex' | 'fold' }) {
  return (
    <svg className="ps-pic" viewBox="0 0 64 56" aria-hidden="true">
      {k === 'paper' && (
        <>
          <path d="M18 6h22l8 8v36H18z" className="pp-sheet" />
          <path d="M40 6v8h8" className="pp-line" />
          <path d="M23 24h18M23 30h18M23 36h12" className="pp-faint" />
        </>
      )}
      {k === 'scale' && (
        <>
          <path d="M16 6h32v44H16z" className="pp-sheet" />
          <text x="32" y="32" textAnchor="middle" dominantBaseline="central" className="pp-text">100%</text>
        </>
      )}
      {k === 'duplex' && (
        <>
          <path d="M24 12h26v38H24z" className="pp-sheet back" />
          <path d="M14 6h26v38H14z" className="pp-sheet" />
          <text x="27" y="25" textAnchor="middle" dominantBaseline="central" className="pp-text small">1</text>
          <path d="M44 4c10 4 12 14 8 22" className="pp-arrow" />
          <path d="M48 24l4 3 2-5" className="pp-arrow" />
        </>
      )}
      {k === 'fold' && (
        <>
          <path d="M14 8h36v40H14z" className="pp-sheet" />
          <path d="M14 34L50 14" className="pp-fold" />
          <path d="M14 34L50 14L50 34z" className="pp-flap" />
          <path d="M40 40c-6 2-12 0-15-5" className="pp-arrow" />
        </>
      )}
    </svg>
  );
}

export function PrintPage({ projectId }: { projectId: string }) {
  const q = useLocation().searchParams;
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [baseTemplate, setTemplate] = useState<TemplateDocument | null>(null);
  const [doc, setDoc] = useState<ProjectDocument | null>(null);
  /** The shapes zones 10-13 can take, and the template with this card's choices in place (ProjectDocument.shapes). */
  const [shapeCat, setShapeCat] = useState<ZoneShapes | null>(null);
  const template = useMemo(() => baseTemplate && withShapes(baseTemplate, shapeCat, doc?.shapes, doc?.slide),
    [baseTemplate, shapeCat, doc?.shapes, doc?.slide]);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [guides, setGuides] = useState<PrintGuides | null>(null);
  // fold guides: on unless turned off (the renderer is told by ?guides=0)
  const [showGuides, setShowGuidesState] = useState(() => {
    try { return localStorage.getItem('fmc.guides') !== '0'; } catch { return true; }
  });
  const setShowGuides = (v: boolean) => {
    setShowGuidesState(v);
    try { localStorage.setItem('fmc.guides', v ? '1' : '0'); } catch { /* a preference only */ }
  };
  const [error, setError] = useState<string | null>(null);
  /** Printing one side only (by hand on a printer without two-sided printing); 0 = both. */
  const [only, setOnly] = useState<0 | 1 | 2>(0);
  /** An invitation (invite.tsx): one card per guest, each with its own name and RSVP code. */
  const ev = useEvent(doc?.invitation?.eventId);
  const printSides = async (side: 0 | 1 | 2) => {
    setOnly(side);
    // let the page drop the other side before the dialog opens; both come back after
    // (Safari's print() does not wait for the dialog, so "after printing" is the cue, not print() returning)
    if (side) addEventListener('afterprint', () => setOnly(0), { once: true });
    setTimeout(() => window.print(), 150);
  };

  useEffect(() => {
    (async () => {
      const { project, versions } = await api.project(projectId);
      const vid = q.get('v') || [...versions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0].id;
      const { version } = await api.version(projectId, vid);
      const [tpl, imgs, g, cat] = await Promise.all([
        api.template(project.templateId, project.paper, version.templateHash), api.images(),
        api.guides(project.templateId, project.paper, version.templateHash),
        api.shapes(project.templateId, project.paper, version.templateHash)]);
      setGuides(g);
      setProject(project);
      setTemplate(tpl);
      setShapeCat(cat);
      setDoc(version.document!);
      setImages(imgs.images);
    })().catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const byId = useMemo(() => new Map(images.map((i) => [i.id, i])), [images]);
  const pictures = useMemo(() => {
    const m = new Map<number, ShownImage>();
    for (const [k, slot] of Object.entries(doc?.zones || {})) {
      const img = slot.image && !slot.plain && byId.get(slot.image.id);
      if (img) m.set(Number(k), shownImage(img, slot));
    }
    return m;
  }, [doc, byId]);
  const notes = useMemo(() => {
    const m = new Map<number, ZoneText>();
    for (const [k, slot] of Object.entries(doc?.zones || {})) if (slot.text && !slot.plain) m.set(Number(k), slot.text);
    return m;
  }, [doc]);
  /** The zones left plain (ZoneSlot.plain): neither picture nor note shows in them. */
  const plain = useMemo(() => new Set(Object.entries(doc?.zones || {}).filter(([, s]) => s.plain).map(([k]) => Number(k))), [doc]);
  useLayouts(template || ({ zones: [] } as unknown as TemplateDocument), notes);

  // every picture decoded and every font in, before anyone prints
  useEffect(() => {
    if (!template || !doc) return;
    let live = true;
    // a browser may hold decoding back (a page in a background tab): never keep the Print button waiting past 8 s
    const decoded = (href: string) => {
      const im = new Image();
      im.src = href;
      return Promise.race([im.decode().catch(() => {}), new Promise((r) => setTimeout(r, 8000))]);
    };
    Promise.all([...pictures.values()].map((p) => decoded(p.href)))
      .then(() => document.fonts.ready)
      .then(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [template, doc, pictures]);

  if (error) return <main className="page"><p className="error">{error}</p></main>;
  if (!template || !doc || !project) return <main className="page"><p className="muted">{t('loading')}</p></main>;
  const inviteZone = doc.invitation?.zone;
  if (inviteZone !== undefined && !ev.data && !ev.error) return <main className="page"><p className="muted">{t('loading')}</p></main>;
  // every guest's copy, in the order of the list; a card that is not an invitation is one copy
  const guests = guestsOn(ev.data, projectId);
  const copies = inviteZone === undefined ? [{ key: 'card', notes, invite: undefined }]
    : guests.length ? guests.map((g) => ({ key: g.id, notes: personalise(notes, g.name), invite: codeFor(inviteZone, g, ev.data?.details ?? null) }))
    : [{ key: 'sample', notes, invite: sampleCode(inviteZone) }];

  const W = template.paper.widthMm, H = template.paper.heightMm;
  const card = { template, pictures, notes, symbols: symbolsOf(doc), paper: doc.paper, mono: doc.mono, plain, selected: null, mode: 'clean' as const };
  const v = q.get('v') ? `?v=${q.get('v')}` : '';
  return (
    <div className="print-root">
      <style>{`@page { size: ${W}mm ${H}mm; margin: 0; }`}</style>
      {/* how to print, as four small pictures across the page (2026-10-17: "the instruction is too long, no one will read it") */}
      <div className="print-help no-print">
        <div className="print-top">
          <Link href={`/projects/${project.id}${v}`} className="button back">← {t('backToCard')}</Link>
          <h1>{t('printTitle')}</h1>
          <span className="row">
            <Link href={`/projects/${project.id}/fold${v}`} className="button primary">{t('nextHowToFold')} ›</Link>
            <TipButton />
            <SignOutButton />
          </span>
        </div>
        <ol className="print-steps">
          <li><PrintPictogram k="paper" /><span className="ps-text"><strong>{t('printPaperShort', { paper: template.paper.name })}</strong><span>{t('printPaperSub')}</span></span></li>
          <li><PrintPictogram k="scale" /><span className="ps-text"><strong>{t('printScaleShort')}</strong><span>{t('printScaleSub')}</span></span></li>
          <li><PrintPictogram k="duplex" /><span className="ps-text"><strong>{t('printDuplexShort')}</strong><span>{t('printDuplexSub')}</span></span></li>
          <li>
            <Link href={`/projects/${project.id}/fold${v}`} className="ps-link">
              <PrintPictogram k="fold" /><span className="ps-text"><strong>{t('printFoldShort')}</strong><span>{t('printFoldSub')} ›</span></span>
            </Link>
          </li>
        </ol>
        {inviteZone !== undefined && (
          <p className="banner invite-banner">{guests.length ? t('invitePrintingFor', { n: guests.length }) : t('inviteNoGuests')}</p>
        )}
        <div className="print-actions">
          <button className="primary big-btn" onClick={() => printSides(0)} disabled={!loaded} title={t('printBothHint')}>
            {loaded ? t('printNow') : t('loading')}
          </button>
          {guides && (
            <label className="switch" title={t('foldGuidesHint')}><input type="checkbox" checked={showGuides}
              onChange={(e) => setShowGuides(e.target.checked)} /> {t('foldGuides')}</label>
          )}
          <span className="print-one small">
            <span className="muted">{t('printNoDuplex')}</span>
            <button className="small" onClick={() => printSides(1)} disabled={!loaded}>{t('printOne1')}</button>
            <button className="small" onClick={() => printSides(2)} disabled={!loaded} title={t('printOneFlip')}>{t('printOne2')}</button>
          </span>
          {/* every design's envelope, made to its folded size (2026-10-18: "create an envelope for each type of cards, so it comes with it") */}
          {envelopeFits(template) && <Link href={`/projects/${project.id}/envelope${v}`} className="button" title={t('printEnvelopeHint')}>✉ {t('printEnvelope')} ›</Link>}
        </div>
      </div>
      {copies.map((c) => ([1, 2] as const).filter((f) => !only || only === f).map((f) => (
        <section key={`${c.key}-${f}`} className="print-sheet" style={{ width: `${W}mm`, height: `${H}mm` }}>
          <SheetFace {...card} notes={c.notes} invite={c.invite} face={f} guides={showGuides ? guides?.faces.find((x) => x.face === f) : null} />
        </section>
      )))}
    </div>
  );
}
